<template>
  <div class="reason-grid">
    <button
      v-for="reason in reasons"
      :key="reason.key"
      class="reason-card"
      @click="$emit('select', reason)"
    >
      <span class="reason-card__icon" :class="reason.iconWrapClass">
        <i v-if="!reason.svg" :class="reason.icon" />
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="3" y1="9" x2="21" y2="9" />
        </svg>
      </span>
      <div class="reason-card__text">
        <span class="reason-card__title">{{ $t(reason.label) }}</span>
        <span class="reason-card__desc">{{ $t(reason.description) }}</span>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
defineEmits<{
  select: [reason: { key: string; label: string; description: string; icon: string }]
}>()

const reasons = [
  {
    key: 'manage_booking',
    label: 'Manage or cancel a booking',
    description: 'Look up your booking to view details, manage, or cancel',
    icon: 'icon-calendar',
    iconWrapClass: 'reason-card__icon--blue',
  },
  {
    key: 'late_cancellation',
    label: 'Request a late cancellation',
    description: 'Need to cancel outside the free cancellation window? We\'ll review your case',
    icon: 'icon-close',
    iconWrapClass: 'reason-card__icon--orange',
  },
  {
    key: 'past_stay_refund',
    label: 'Request a refund for a past stay',
    description: 'Had an issue during your stay? Submit a refund request',
    icon: 'icon-dollar',
    iconWrapClass: 'reason-card__icon--green',
  },
  {
    key: 'payment_issue',
    label: 'Payment issue',
    description: 'Charged incorrectly or having trouble with a payment?',
    icon: 'icon-creditcard',
    iconWrapClass: 'reason-card__icon--red',
  },
  {
    key: 'partner_inquiry',
    label: 'List your property',
    description: 'Interested in partnering with us? Get in touch',
    svg: true,
    icon: '',
    iconWrapClass: 'reason-card__icon--purple',
  },
  {
    key: 'general_inquiry',
    label: 'General inquiry',
    description: 'Have a question? We\'re here to help',
    icon: 'icon-info',
    iconWrapClass: 'reason-card__icon--grey',
  },
]
</script>

<style lang="scss" scoped>
.reason-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
}

.reason-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px;
  border: 1px solid var(--grey-border);
  border-radius: 6px;
  background: var(--white);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: var(--blue);
    background-color: var(--light-grey);
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;

    &--blue {
      background: rgba(25, 133, 249, 0.1);
      color: #1985F9;
    }

    &--orange {
      background: rgba(237, 137, 54, 0.1);
      color: #ED8936;
    }

    &--green {
      background: rgba(72, 187, 120, 0.1);
      color: #48BB78;
    }

    &--red {
      background: rgba(245, 101, 101, 0.1);
      color: #F56565;
    }

    &--purple {
      background: rgba(128, 90, 213, 0.1);
      color: #805AD5;
    }

    &--grey {
      background: rgba(89, 87, 87, 0.1);
      color: var(--dark-grey);
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: var(--black);
    line-height: 1.3;
  }

  &__desc {
    font-size: 14px;
    font-weight: 400;
    color: var(--dark-grey);
    line-height: 1.4;
  }
}
</style>
